import React, { useState, useEffect, useRef, useCallback } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { IoCloseSharp } from "react-icons/io5";
import DatePicker from "react-datepicker";
import { deleteByFile, searchDetail } from "../../api/fileAPI";
import SortButton from "./sortButton";
import Pagination from "./Pagination";
import { FaInfoCircle, FaMinusCircle, FaRegCalendarAlt, FaUserAlt } from "react-icons/fa";
import { MdGroups } from "react-icons/md";
import { AiFillFileText } from "react-icons/ai";
import { BiExtension } from "react-icons/bi";
import { HiOutlineDatabase } from "react-icons/hi";
import dayjs from "dayjs";
import Swal from "sweetalert2";

interface PropFileContent {
  totalSize: number;
  maxFileSize: number;
  totalFiles: number;
  userLastLoginDate?: number;
  userId?: string;
  nickName?: string;
  displayMonth?: string;
  displayDate?: string;
  courseId?: string;
  courseName?: string;
  totalTeams?: number;
  teamCreationDate?: number;
  startTime?: number;
  endTime?: number;
}

interface FileContent {
  id: string;
  name: string;
  extension: string;
  size: number;
  mimeType: string;
  path: string;
  thumbnailPath: string;
  previewPath: string;
  createAt: number;
  updateAt: number;
  creatorId: string;
  creatorUsername: string;
  creatorNickname: string;
  teamId: string;
  teamName: string | null;
  channelId: string;
}

interface FileDetailModalProps {
  isOpen: boolean;
  onClose: (state: boolean) => void;
  propData: PropFileContent | null;
}

interface PageInfo {
  size: number;
  number: number;
  totalElements: number;
  totalPages: number;
}

const FileDetailModal: React.FC<FileDetailModalProps> = ({ isOpen, onClose, propData }) => {
  const handleModal = () => {
    onClose(false);
  };

  const reset = () => {
    //검색 조건 초기화
    setStartDate(new Date(today.getFullYear() - 3, today.getMonth(), today.getDate()));
    setEndDate(today);
    setNickname("");
    setSearchTeamName("");
    setCurrentPage(0);
    setSelectedRow(-1);
    setSortState("");
    setSelectedData([]);
  };

  useEffect(() => {
    if (isOpen) {
      if (propData) {
        if (propData.userId) {
          detailFileSearch({ creatorNickname: propData.nickName, creatorId: propData.userId });
          setNickname(propData.nickName ? propData.nickName : `[익명]`);
        } else if (propData.courseName) {
          detailFileSearch({ teamId: propData.courseId, teamName: propData.courseName });
          setSearchTeamName(propData.courseName);
        } else if (propData.startTime && propData.endTime) {
          detailFileSearch({ createAtFrom: propData.startTime, createAtTo: propData.endTime });
          setStartDate(new Date(propData.startTime));
          setEndDate(new Date(propData.endTime));
        }
      } else {
        detailFileSearch({});
      }
    } else {
      reset(); // 모달이 닫힐 때 reset() 함수 호출
    }
  }, [isOpen]);

  type DetailFileSearchParams = {
    creatorId?: string;
    creatorNickname?: string;
    createAtFrom?: number;
    createAtTo?: number;
    teamId?: string;
    teamName?: string;
    page?: number;
    size?: number;
    sort?: string;
  };

  const formatMB = (bytes: number) => +(bytes / 1024 / 1024).toFixed(2);

  // 상태 변수들을 먼저 선언
  const [files, setFiles] = useState<FileContent[]>([]);
  const [nickname, setNickname] = useState<string>("");
  const [searchTeamName, setSearchTeamName] = useState<string>("");
  const [pageInfo, setPageInfo] = useState<PageInfo>({
    size: 50,
    number: 0,
    totalElements: 0,
    totalPages: 0,
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [currentSize, setCurrentSize] = useState(50);
  const today = new Date();
  const [startDate, setStartDate] = useState<Date>(new Date(today.getFullYear() - 3, today.getMonth(), today.getDate()));
  const [endDate, setEndDate] = useState<Date>(new Date());
  const scrollbarRef = useRef<HTMLElement | null>(null);
  const [selectedRow, setSelectedRow] = useState<number>(-1);
  const [selectedData, setSelectedData] = useState<FileContent[]>([]);
  const [allCheck, setAllCheck] = useState<boolean>(false);
  const [sortState, setSortState] = useState<string>("");
  const [fileViewMode, setFileViewMode] = useState<"active" | "deleted">("active");

  const scrollUp = useCallback(() => {
    if (scrollbarRef.current) {
      scrollbarRef.current.scrollTop = 0;
    }
  }, []);

  const handleSelectedRow = useCallback((id: number) => {
    setSelectedRow(id);
  }, []);

  //파일 상세 검색 - useCallback으로 메모이제이션하여 불필요한 재생성 방지
  const detailFileSearch = useCallback(async (params: DetailFileSearchParams) => {
    const {
      creatorId = "",
      creatorNickname = nickname,
      createAtFrom = startDate.getTime(),
      createAtTo = endDate.getTime(),
      teamId = "",
      teamName = searchTeamName,
      page = currentPage,
      size = currentSize,
      sort = sortState,
    } = params;

    const data = await searchDetail(creatorId, creatorNickname, createAtFrom, createAtTo, teamId, teamName, page, size, sort);

    if (data) {
      setFiles(
        data.data.content.map((file: any) => ({
          ...file,
          size: formatMB(file.size),
        }))
      );
      setPageInfo(data.data.page);
      setCurrentPage(data.data.page.number);
      setCurrentSize(data.data.page.size);
      scrollUp();
      handleSelectedRow(-1);
      setSelectedData([]);
      setAllCheck(false);
    }
  }, [nickname, startDate, endDate, searchTeamName, currentSize, sortState, handleSelectedRow, scrollUp]);

  // 파일 삭제
  const fileDelete = async () => {
    const data = await deleteByFile(selectedData.map((file) => file.id));

    if (data) {
      detailFileSearch({});
      deleteAccessAlert();
    }
  };

  // 삭제 경고창
  const handleDeleteConfirm = () => {
    Swal.fire({
      title: `정말 선택한 데이터들을 \n삭제하시겠습니까??`,
      text: `총 ${selectedData.reduce((acc, file) => acc + file.size, 0).toLocaleString()} MB, \n      ${selectedData.length.toLocaleString()}개의 파일입니다.`,
      icon: "warning",
      showCancelButton: true,
      color: "red",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "예",
      cancelButtonText: "아니오",
    }).then((result) => {
      if (result.isConfirmed) {
        fileDelete();
      }
    });
  };

  // 삭제 성공 알림
  const deleteAccessAlert = () => {
    Swal.fire({
      title: "성공",
      text: "삭제가 성공적으로 완료되었습니다.",
      icon: "success",
      confirmButtonText: "확인",
      timer: 3000,
    });
  };

  useEffect(() => {
    if (isOpen) {
      detailFileSearch({ page: 0 });
    }
  }, [currentSize, detailFileSearch]);

  const handlePageChange = useCallback((page: number) => {
    if (page >= 0 && page < pageInfo.totalPages) {
      // 페이지 변경 시 직접 검색 호출하여 불필요한 useEffect 실행 방지
      detailFileSearch({ page });
    }
  }, [pageInfo.totalPages, detailFileSearch]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      detailFileSearch({});
    }
  };

  const handleNickname = (str: string) => {
    setNickname(str);
  };

  const handleTeamName = (str: string) => {
    setSearchTeamName(str);
  };

  const handleStartDateChange = (date: Date) => {
    setStartDate(date);
    if (endDate && date && date > endDate) {
      setEndDate(date);
    }
  };
  const handleEndDateChange = (date: Date) => {
    if (startDate && date && date < startDate) {
      alert("종료 날짜는 시작 날짜 이후여야 합니다.");
      return;
    }
    setEndDate(date);
  };


  const handleAllCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAllCheck(event.target.checked);
  };

  const choiceFileAdd = (file: FileContent) => {
    if (file && !selectedData.includes(file)) {
      setSelectedData([...selectedData, file]);
    }
  };

  const choiceFileRemove = (file: FileContent) => {
    setSelectedData(selectedData.filter((f) => f !== file));
    setAllCheck(false);
  };

  useEffect(() => {
    if (allCheck) {
      setSelectedData(files);
    } else {
      if (selectedData.length === files.length && files.length > 0) {
        setSelectedData([]);
      }
    }
  }, [allCheck]);

  const handleSortState = (state: string) => {
    setSortState(state);
  };

  useEffect(() => {
    if (isOpen) {
      detailFileSearch({});
    }
  }, [sortState]);

  const [tooltip, setTooltip] = useState<{
    content: React.ReactNode;
    top: number;
    left: number;
  } | null>(null);

  const handleMouseOver = (e: React.MouseEvent<SVGSVGElement, MouseEvent>, file: FileContent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      content: (
        <>
          <p>사용자: {file.creatorNickname}</p>
          <p>팀: {file.teamName || "-"}</p>
          <p>확장자: {file.extension}</p>
          <p>크기: {file.size.toLocaleString()} MB</p>
          <p>생성일: {dayjs(file.createAt).format("YYYY-MM-DD")}</p>
        </>
      ),
      top: rect.top,
      left: rect.right + 5,
    });
  };

  const handleMouseOut = () => {
    setTooltip(null);
  };

  if (!isOpen) return null;

  return (
    <div>
      {tooltip && (
        <div
          className="fixed p-2 text-xs text-white bg-black rounded-md z-[9999]"
          style={{ top: tooltip.top, left: tooltip.left }}
        >
          {tooltip.content}
        </div>
      )}
      <div className="font-Noto_Sans_KR">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-60 backdrop-blur-sm"
          onClick={handleModal}
        ></div>

        <div className="fixed inset-0 z-10 m-5" onClick={handleModal}>
          <div className="mx-auto min-w-[96.2vw] max-w-[96.2vw]" onClick={(e) => e.stopPropagation()}>
            <div className="p-3 mb-2 bg-white rounded-lg shadow-sm">
              <div className="flex items-center justify-between gap-4">
                <div className="">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</div>
                <div className="text-2xl font-bold">파일 상세 검색</div>
                <div className="relative flex items-center justify-center">
                  <div className="ml-3">
                    <button type="button" className="text-gray-400 hover:text-gray-500" onClick={handleModal}>
                      <span className="sr-only">닫기</span>
                      <IoCloseSharp size={30} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="min-w-[20vw] max-w-[20vw] bg-white rounded-lg shadow-sm p-4 flex flex-col">
                <div className="flex-grow">
                  <div className="flex-1 py-2 space-y-3">
                    <div className="flex flex-col items-start">
                      <span className="mr-2 text-base text-gray-500 whitespace-nowrap">사용자</span>
                      <input
                        type="text"
                        placeholder="사용자 검색"
                        value={nickname}
                        onChange={(e) => handleNickname(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md"
                        onKeyDown={handleSearchKeyDown}
                      />
                    </div>
                    <div className="flex flex-col items-start">
                      <span className="mr-2 text-base text-gray-500 whitespace-nowrap">팀이름</span>
                      <input
                        type="text"
                        placeholder="팀 검색"
                        value={searchTeamName}
                        onChange={(e) => handleTeamName(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md"
                        onKeyDown={handleSearchKeyDown}
                      />
                    </div>
                    <div className="flex flex-col items-start">
                      <label className="mr-2 text-base text-gray-500 whitespace-nowrap">시작 날짜</label>
                      <DatePicker
                        selected={startDate}
                        onChange={handleStartDateChange}
                        dateFormat="yyyy-MM-dd"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm"
                        maxDate={endDate}
                      />
                    </div>
                    <div className="flex flex-col items-start">
                      <label className="mr-2 text-base text-gray-500 whitespace-nowrap">종료 날짜</label>
                      <DatePicker
                        selected={endDate}
                        onChange={handleEndDateChange}
                        dateFormat="yyyy-MM-dd"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm"
                        minDate={startDate}
                      />
                    </div>
                  </div>

                  <div className="flex justify-between mt-4 mb-2 space-x-3">
                    <button
                      onClick={() => detailFileSearch({})}
                      className="w-1/2 px-4 py-2 text-white bg-black rounded-md hover:bg-gray-700"
                    >
                      검색
                    </button>
                    <button onClick={reset} className="w-1/2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                      초기화
                    </button>
                  </div>

                  <div className="mt-4 border-t pt-2">
                    <h3 className="text-sm font-semibold text-gray-600">선택된 파일 ({selectedData.length}개)</h3>
                    <PerfectScrollbar style={{ height: "200px", minHeight: "100px" }}>
                      <ul className="mt-2 space-y-1">
                        {selectedData.map((file) => (
                          <li key={file.id} className="flex items-center justify-between p-1 text-sm bg-gray-100 rounded">
                            <span className="truncate w-48" title={file.name}>
                              {file.name}
                            </span>
                            <div className="flex items-center space-x-2">
                              <FaInfoCircle
                                className="text-gray-500 cursor-pointer"
                                onMouseEnter={(e) => handleMouseOver(e, file)}
                                onMouseLeave={handleMouseOut}
                              />
                              <button
                                onClick={() => choiceFileRemove(file)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <FaMinusCircle />
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </PerfectScrollbar>
                  </div>
                </div>

                <div className="flex-shrink-0 mt-4">
                  <button
                    onClick={() => {
                      if (selectedData.length > 0) {
                        handleDeleteConfirm();
                      }
                    }}
                    className={`w-full px-4 py-2 text-lg font-bold text-white rounded-md ${
                      selectedData.length > 0
                        ? "bg-red-500 hover:bg-red-400"
                        : "bg-gray-400 cursor-not-allowed"
                    }`}
                    disabled={selectedData.length === 0}
                  >
                    {selectedData.length > 0
                      ? `선택 ${selectedData.length.toLocaleString()}개 (${selectedData
                          .reduce((acc, file) => acc + file.size, 0)
                          .toLocaleString()} MB) 삭제`
                      : "삭제"}
                  </button>
                </div>
              </div>

              <div className="min-w-[75vw] max-w-[75vw] bg-white rounded-lg shadow-sm p-4">
                <div>
                  <PerfectScrollbar containerRef={(ref) => (scrollbarRef.current = ref)}>
                    <div className="min-h-[650px] max-h-[650px]">
                      <table className="min-w-full border-collapse divide-y divide-gray-200">
                        <thead className="sticky top-0 bg-gray-50">
                          <tr className="border-b border-gray-200">
                            <th className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                              <input
                                type="checkbox"
                                className="border-gray-300 rounded hover:cursor-pointer"
                                checked={allCheck}
                                onChange={handleAllCheck}
                              />
                            </th>
                            <th className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-x-2">
                              <div className="flex w-full">
                                <div className="pt-0.5 mr-2">
                                  <FaUserAlt />
                                </div>
                                <SortButton
                                  title="사용자"
                                  sortState={sortState}
                                  sortList={["", "creatorNickname,asc", "creatorNickname,desc"]}
                                  handleState={handleSortState}
                                />
                              </div>
                            </th>
                            <th className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-x-2">
                              <div className="flex w-full">
                                <div className="pt-0.5 mr-2">
                                  <MdGroups />
                                </div>
                                <SortButton
                                  title="팀이름"
                                  sortState={sortState}
                                  sortList={["", "teamName,asc", "teamName,desc"]}
                                  handleState={handleSortState}
                                />
                              </div>
                            </th>
                            <th className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-x-2">
                              <div className="flex w-full">
                                <div className="pt-0.5 mr-2">
                                  <AiFillFileText />
                                </div>
                                <SortButton
                                  title="파일명"
                                  sortState={sortState}
                                  sortList={["", "name,asc", "name,desc"]}
                                  handleState={handleSortState}
                                />
                              </div>
                            </th>
                            <th className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-x-2">
                              <div className="flex w-full">
                                <div className="pt-0.5 mr-2">
                                  <BiExtension />
                                </div>
                                <SortButton
                                  title="확장자"
                                  sortState={sortState}
                                  sortList={["", "extension,asc", "extension,desc"]}
                                  handleState={handleSortState}
                                />
                              </div>
                            </th>
                            <th className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-x-2">
                              <div className="flex w-full">
                                <div className="pt-0.5 mr-2">
                                  <HiOutlineDatabase />
                                </div>
                                <SortButton
                                  title="파일 크기"
                                  sortState={sortState}
                                  sortList={["", "size,asc", "size,desc"]}
                                  handleState={handleSortState}
                                />
                              </div>
                            </th>
                            <th className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-x-2">
                              <div className="flex w-full">
                                <div className="pt-0.5 mr-2">
                                  <FaRegCalendarAlt />
                                </div>
                                <SortButton
                                  title="업로드 날짜"
                                  sortState={sortState}
                                  sortList={["", "createAt,asc", "createAt,desc"]}
                                  handleState={handleSortState}
                                />
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200 round">
                          {files.map((file, idx) => (
                            <tr
                              className={`${selectedRow === idx ? "bg-orange-100" : ""}`}
                              key={idx}
                              onClick={() => {
                                if (selectedRow === idx) {
                                  handleSelectedRow(-1);
                                } else {
                                  handleSelectedRow(idx);
                                }
                              }}
                            >
                              <td className="px-3 py-2 whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  className="border-gray-300 rounded hover:cursor-pointer"
                                  checked={selectedData.includes(file)}
                                  onChange={() => {
                                    if (selectedData.includes(file)) {
                                      choiceFileRemove(file);
                                    } else {
                                      choiceFileAdd(file);
                                    }
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                />
                              </td>
                              <td className="px-3 py-2 min-w-[7vw] max-w-[7vw] border-x-2">
                                <div className="text-sm text-gray-900 truncate">
                                  {file.creatorNickname ? `${file.creatorNickname}` : `[익명] (${file.creatorUsername})`}
                                </div>
                              </td>
                              <td
                                className={`px-3 py-2 min-w-[12vw] max-w-[12vw] border-x-2 ${
                                  file.teamName ? "text-start" : "text-center"
                                }`}
                              >
                                <div className="text-sm text-gray-900 truncate">{file.teamName ? file.teamName : "-"}</div>
                              </td>
                              <td className="px-3 py-2 min-w-[10vw] max-w-[10vw] border-x-2">
                                <div className="text-sm text-gray-900 truncate">{file.name}</div>
                              </td>
                              <td className="px-3 py-2 min-w-[3vw] max-w-[3vw] border-x-2 text-end">
                                <div className="text-sm text-gray-900 truncate">{file.extension}</div>
                              </td>
                              <td className="px-3 py-2 min-w-[4vw] max-w-[4vw] border-x-2 text-end">
                                <div className="text-sm text-gray-900 truncate">{file.size.toLocaleString()} MB</div>
                              </td>
                              <td className="px-3 py-2 min-w-[4vw] max-w-[4vw] border-x-2">
                                <div className="text-sm text-gray-900 truncate">
                                  {dayjs(file.createAt).format("YYYY-MM-DD")}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </PerfectScrollbar>
                </div>
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-center">
                    <Pagination
                      currentPage={currentPage}
                      totalPage={pageInfo.totalPages}
                      handlePageChange={handlePageChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileDetailModal;